import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  Link as MUILink,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Drawer,
} from '@mui/material';
import {
  Close,
  Edit,
  Home,
  MailOutline,
  Pause,
  PlayArrow,
  ScheduleSend,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  useCreateEmailSchedule,
  useUpdateEmailSchedule,
  useEmailSchedules,
  useOutboundEmails,
  useSmtpConfig,
  useSaveSmtpConfig,
  useEmailTemplates,
  useUpdateEmailTemplate,
} from '@/hooks';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import type {
  EmailSchedule,
  EmailScheduleFrequency,
  EmailScheduleInput,
} from '@/types/emailSchedule';
import type { EmailSmtpConfigInput, EmailTemplate } from '@/types/emailAdmin';
import { getErrorMessage } from '@/utils/httpClient';
import { usePageTitle } from '@/hooks/usePageTitle';
import { buildTimezoneOptions, createTimezoneOption, DEFAULT_TIMEZONE_ID } from '@/utils/timezones';
import type { TimezoneOption } from '@/utils/timezones';
import { useNotifications } from '@/stores/useNotificationStore';
import { logger } from '@equip/utils/logger';
import { OutboundEmailAttachmentList } from '@/components/email/OutboundEmailAttachmentList';

const log = logger.child({ component: 'EmailSchedulesPage' });

dayjs.extend(relativeTime);

const DAYS_OF_WEEK: Array<{ key: string; label: string }> = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const DEFAULT_WEEKLY_DAYS = ['mon', 'wed', 'fri'] as const;

type ScheduleTemplate = {
  value: string;
  label: string;
  description: string;
  conditionKey: string;
  defaultCondition?: Record<string, unknown>;
  recipientMode?: 'auto' | 'optional' | 'required';
  defaultFrequency?: EmailScheduleFrequency;
  singleInstance?: boolean;
};

const SCHEDULE_TEMPLATES: ScheduleTemplate[] = [
  {
    value: 'thr_approaching_return_date_reminder',
    label: 'THR – Approaching return reminder',
    description: 'Daily reminder when THR items are due within the next N days.',
    conditionKey: 'thr_due_soon',
    defaultCondition: { days_out: 14 },
    recipientMode: 'auto',
    defaultFrequency: 'daily',
    singleInstance: true,
  },
  {
    value: 'thr_overdue_return_alert',
    label: 'THR – Overdue return alert',
    description: 'Daily alert for THR items whose due date has passed.',
    conditionKey: 'thr_overdue',
    defaultCondition: {},
    recipientMode: 'auto',
    defaultFrequency: 'daily',
    singleInstance: true,
  },
  {
    value: 'stock_below_threshold',
    label: 'Reorder threshold alert',
    description: 'Daily digest of catalog items that are at or below their reorder thresholds.',
    conditionKey: 'stock_below_threshold',
    defaultCondition: {},
    recipientMode: 'required',
    defaultFrequency: 'daily',
    singleInstance: true,
  },
];

const getEffectiveWeeklyDays = (days?: string[]) =>
  days && days.length > 0 ? days : [...DEFAULT_WEEKLY_DAYS];

const normalizeTime = (value: string): string => {
  const [hours = '07', minutes = '30'] = (value || '').split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

const getTemplateByValue = (value?: string): ScheduleTemplate =>
  SCHEDULE_TEMPLATES.find((template) => template.value === value) ?? SCHEDULE_TEMPLATES[0]!;

const getRecipientMode = (
  template: ScheduleTemplate,
): NonNullable<ScheduleTemplate['recipientMode']> => template.recipientMode ?? 'auto';

const describeTemplate = (
  template: ScheduleTemplate,
  condition?: Record<string, unknown> | null,
): string => {
  if (template.value === 'stock_below_threshold') {
    return 'Daily digest listing catalog items below their reorder thresholds.';
  }
  if (template.value !== 'thr_approaching_return_date_reminder') return template.description;
  const daysOutSource = condition ?? template.defaultCondition ?? {};
  const daysOut = Number(daysOutSource['days_out']);
  const resolved = Number.isFinite(daysOut) ? daysOut : 'N';
  const plural = resolved === 1 ? '' : 's';
  return `Daily reminder when THR items are due within the next ${resolved} day${plural}.`;
};

const renderFrequency = (
  frequency: EmailScheduleFrequency,
  days: string[],
  dayOfMonth?: number | null,
): string => {
  if (frequency === 'daily') return 'Daily';
  if (frequency === 'weekly') return `Weekly on ${days.map((d) => d.toUpperCase()).join(', ')}`;
  return `Monthly on day ${dayOfMonth ?? 1}`;
};

const formatRecipients = (schedule: EmailSchedule | null, template: ScheduleTemplate): string => {
  const mode = getRecipientMode(template);
  if (!schedule) return mode === 'auto' ? 'Auto-resolved from THR borrowers' : '—';

  if (schedule.recipient_emails?.length) return schedule.recipient_emails.join(' ');
  if (schedule.recipient_addresses?.trim()) return schedule.recipient_addresses;
  if (mode === 'auto') return 'Auto-resolved from THR borrowers';
  return '—';
};

const buildDefaultForm = (
  timezone: string = DEFAULT_TIMEZONE_ID,
  template: ScheduleTemplate = SCHEDULE_TEMPLATES[0]!,
): EmailScheduleInput & { condition_key: string; event_type: string } => ({
  name: template.label,
  description: template.description,
  recipient_addresses: '',
  frequency: template.defaultFrequency ?? 'daily',
  days_of_week: template.defaultFrequency === 'weekly' ? [...DEFAULT_WEEKLY_DAYS] : [],
  day_of_month: template.defaultFrequency === 'monthly' ? 1 : undefined,
  send_time: '07:30',
  timezone: timezone || DEFAULT_TIMEZONE_ID,
  enabled: true,
  condition: template.defaultCondition ?? {},
  condition_key: template.conditionKey,
  event_type: template.value,
});

const buildFormFromSchedule = (
  schedule: EmailSchedule,
): EmailScheduleInput & { condition_key: string; event_type: string } => ({
  name: schedule.name,
  description: schedule.description ?? '',
  recipient_addresses: schedule.recipient_addresses,
  frequency: schedule.frequency,
  days_of_week: [...schedule.days_of_week],
  day_of_month: schedule.day_of_month ?? undefined,
  send_time: normalizeTime(schedule.send_time),
  timezone: schedule.timezone,
  enabled: schedule.enabled,
  condition: schedule.condition ?? {},
  condition_key: schedule.condition_key,
  event_type: schedule.event_type,
});

type SingleScheduleSummaryProps = {
  template: ScheduleTemplate;
  schedule: EmailSchedule | null;
  onEdit: () => void;
  onCreate: () => void;
  onToggle: (schedule: EmailSchedule) => Promise<void> | void;
  isUpdating: boolean;
  updatingScheduleId: string | null;
  isLoading: boolean;
  isCreating: boolean;
};

const SingleScheduleSummary: React.FC<SingleScheduleSummaryProps> = ({
  template,
  schedule,
  onEdit,
  onCreate,
  onToggle,
  isUpdating,
  updatingScheduleId,
  isLoading,
  isCreating,
}) => {
  const lastRun = schedule?.last_run_at ? dayjs(schedule.last_run_at).fromNow() : '—';
  const nextRun = schedule?.next_run_at
    ? dayjs(schedule.next_run_at).format('MMM D, YYYY h:mm A')
    : '—';
  const recipients = formatRecipients(schedule, template);

  return (
    <>
      {schedule ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1.5fr 2fr 1fr 0.8fr' },
            gap: 2,
            rowGap: 3,
          }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary" component="div">
              Cadence
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {renderFrequency(schedule.frequency, schedule.days_of_week, schedule.day_of_month)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              at {schedule.send_time} ({schedule.timezone})
            </Typography>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" component="div">
              Recipients
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {recipients}
            </Typography>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" component="div">
              Run History
            </Typography>
            <Stack spacing={0}>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                  Last:
                </Box>
                {lastRun}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                  Next:
                </Box>
                {nextRun}
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" component="div">
              State
            </Typography>
            <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
              <Chip
                size="small"
                label={schedule.enabled ? 'Active' : 'Paused'}
                color={schedule.enabled ? 'success' : 'default'}
                variant={schedule.enabled ? 'filled' : 'outlined'}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Not configured yet. Create a schedule to start sending this reminder.
        </Typography>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
        {schedule ? (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={schedule.enabled ? <Pause /> : <PlayArrow />}
              onClick={() => onToggle(schedule)}
              disabled={isUpdating && updatingScheduleId === schedule.id}
              sx={{ minWidth: 110 }}
            >
              {schedule.enabled ? 'Pause' : 'Resume'}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Edit />}
              onClick={onEdit}
              disabled={isUpdating && updatingScheduleId === schedule.id}
            >
              Edit
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrow />}
            onClick={onCreate}
            disabled={isLoading || isCreating}
          >
            Create schedule
          </Button>
        )}
      </Stack>
    </>
  );
};

type MultiScheduleSummaryProps = {
  template: ScheduleTemplate;
  schedules: EmailSchedule[];
  onEdit: (schedule: EmailSchedule) => void;
  onToggle: (schedule: EmailSchedule) => Promise<void> | void;
  onCreate: () => void;
  isUpdating: boolean;
  updatingScheduleId: string | null;
  isLoading: boolean;
  isCreating: boolean;
};

const MultiScheduleSummary: React.FC<MultiScheduleSummaryProps> = ({
  template,
  schedules,
  onEdit,
  onToggle,
  onCreate,
  isUpdating,
  updatingScheduleId,
  isLoading,
  isCreating,
}) => {
  return (
    <Stack spacing={1.5}>
      {schedules.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No schedules yet. Create one to start sending this alert.
        </Typography>
      ) : (
        schedules.map((schedule) => {
          const lastRun = schedule.last_run_at ? dayjs(schedule.last_run_at).fromNow() : '—';
          const nextRun = schedule.next_run_at
            ? dayjs(schedule.next_run_at).format('MMM D, YYYY h:mm A')
            : '—';
          const recipients = formatRecipients(schedule, template);

          return (
            <Paper key={schedule.id} variant="outlined" sx={{ p: 1.5 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Stack spacing={0.25}>
                  <Typography variant="subtitle1">{schedule.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {describeTemplate(template, schedule.condition)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={schedule.enabled ? <Pause /> : <PlayArrow />}
                    onClick={() => onToggle(schedule)}
                    disabled={isUpdating && updatingScheduleId === schedule.id}
                    sx={{ minWidth: 110 }}
                  >
                    {schedule.enabled ? 'Pause' : 'Resume'}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => onEdit(schedule)}
                    disabled={isUpdating && updatingScheduleId === schedule.id}
                  >
                    Edit
                  </Button>
                </Stack>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1.5fr 2fr 1fr 0.8fr' },
                  gap: 2,
                  rowGap: 3,
                }}
              >
                <Box>
                  <Typography variant="overline" color="text.secondary" component="div">
                    Cadence
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {renderFrequency(
                      schedule.frequency,
                      schedule.days_of_week,
                      schedule.day_of_month,
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    at {schedule.send_time} ({schedule.timezone})
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="overline" color="text.secondary" component="div">
                    Recipients
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {recipients}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="overline" color="text.secondary" component="div">
                    Run History
                  </Typography>
                  <Stack spacing={0}>
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                        Last:
                      </Box>
                      {lastRun}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                        Next:
                      </Box>
                      {nextRun}
                    </Typography>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="overline" color="text.secondary" component="div">
                    State
                  </Typography>
                  <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                    <Chip
                      size="small"
                      label={schedule.enabled ? 'Active' : 'Paused'}
                      color={schedule.enabled ? 'success' : 'default'}
                      variant={schedule.enabled ? 'filled' : 'outlined'}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>
          );
        })
      )}

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          size="small"
          startIcon={<PlayArrow />}
          onClick={onCreate}
          disabled={isLoading || isCreating}
        >
          Create schedule
        </Button>
      </Stack>
    </Stack>
  );
};

const SchedulesTab: React.FC = () => {
  const userTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone ?? DEFAULT_TIMEZONE_ID,
    [],
  );
  const [formState, setFormState] = useState(() => buildDefaultForm(userTimezone));
  const [editingTemplate, setEditingTemplate] = useState<ScheduleTemplate | null>(null);
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [updatingScheduleId, setUpdatingScheduleId] = useState<string | null>(null);
  const [lastSavedMessage, setLastSavedMessage] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { data: schedules = [], isLoading, error: loadError } = useEmailSchedules();
  const {
    mutateAsync: createSchedule,
    isPending: isCreating,
    reset: resetCreate,
    error: createError,
  } = useCreateEmailSchedule();
  const { mutateAsync: updateSchedule, isPending: isUpdating } = useUpdateEmailSchedule();
  const timezoneOptions = useMemo(
    () => buildTimezoneOptions([userTimezone, formState.timezone]),
    [userTimezone, formState.timezone],
  );
  const currentTimezone = formState.timezone || DEFAULT_TIMEZONE_ID;
  const templateInEditor = useMemo(
    () => editingTemplate ?? getTemplateByValue(formState.event_type),
    [editingTemplate, formState.event_type],
  );
  const recipientMode = getRecipientMode(templateInEditor);
  const recipientInputEnabled = recipientMode !== 'auto';
  const recipientInputRequired = recipientMode === 'required';

  const handleFrequencyChange = (value: EmailScheduleFrequency) => {
    setFormState((prev) => ({
      ...prev,
      frequency: value,
      // Preserve or restore a valid weekly selection when toggling cadences
      days_of_week: [...getEffectiveWeeklyDays(prev.days_of_week)],
      // Ensure monthly schedules always carry a concrete day value
      day_of_month: value === 'monthly' ? (prev.day_of_month ?? 1) : undefined,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setValidationError(null);

    const mergedCondition: Record<string, unknown> =
      templateInEditor.value === 'stock_below_threshold'
        ? {}
        : { ...(templateInEditor.defaultCondition ?? {}), ...(formState.condition ?? {}) };

    const inferredName = formState.name.trim() || templateInEditor.label;

    const recipientAddresses = recipientInputEnabled ? formState.recipient_addresses.trim() : '';

    if (recipientInputRequired && recipientAddresses.length === 0) {
      setValidationError('Add at least one recipient email.');
      return;
    }

    const payload: EmailScheduleInput = {
      name: formState.name.trim() || inferredName,
      description: formState.description,
      recipient_addresses: recipientAddresses,
      frequency: formState.frequency,
      days_of_week:
        formState.frequency === 'weekly' ? [...getEffectiveWeeklyDays(formState.days_of_week)] : [],
      // Fallback to 1 if user leaves the displayed default untouched
      day_of_month: formState.frequency === 'monthly' ? (formState.day_of_month ?? 1) : undefined,
      send_time: normalizeTime(formState.send_time),
      timezone: formState.timezone || DEFAULT_TIMEZONE_ID,
      enabled: formState.enabled,
      condition_key: templateInEditor.conditionKey,
      event_type: templateInEditor.value,
      condition: mergedCondition,
    };

    try {
      if (editingScheduleId) {
        setUpdatingScheduleId(editingScheduleId);
        await updateSchedule({ id: editingScheduleId, input: payload });
      } else {
        const created = await createSchedule(payload);
        setEditingScheduleId(created.id);
      }
      setFormState((prev) => ({
        ...prev,
        ...payload,
        event_type: templateInEditor.value,
        condition_key: templateInEditor.conditionKey,
      }));
      setLastSavedMessage(
        `${templateInEditor.label} saved${payload.name ? ` · ${payload.name.trim()}` : ''}`,
      );
      resetCreate();
    } catch (err) {
      log.error('Failed to save email schedule', { error: String(err) });
    } finally {
      setUpdatingScheduleId(null);
    }
  };

  const toggleEnabled = async (schedule: EmailSchedule) => {
    setUpdatingScheduleId(schedule.id);
    try {
      await updateSchedule({ id: schedule.id, input: { enabled: !schedule.enabled } });
    } finally {
      setUpdatingScheduleId(null);
    }
  };

  const statusMessage = useMemo(() => {
    // Avoid showing "Refreshing..." during background updates to prevent layout shift/flutter.
    // The optimistic UI updates provide sufficient feedback.
    if (isLoading) return 'Loading schedules...';
    return null;
  }, [isLoading]);

  const schedulesByEventType = useMemo(() => {
    const map = new Map<string, EmailSchedule[]>();
    for (const schedule of schedules) {
      const list = map.get(schedule.event_type) ?? [];
      list.push(schedule);
      map.set(schedule.event_type, list);
    }
    return map;
  }, [schedules]);

  const openEditor = (template: ScheduleTemplate, schedule?: EmailSchedule) => {
    const nextForm = schedule
      ? buildFormFromSchedule(schedule)
      : buildDefaultForm(userTimezone, template);
    setFormState(nextForm);
    setEditingTemplate(template);
    setEditingScheduleId(schedule?.id ?? null);
    setLastSavedMessage(null);
    setValidationError(null);
  };

  const closeEditor = () => {
    setEditingTemplate(null);
    setEditingScheduleId(null);
    setLastSavedMessage(null);
    setValidationError(null);
  };

  return (
    <>
      <Stack spacing={3} sx={{ mt: 2 }}>
        <Stack spacing={0.5}>
          {statusMessage ? <Typography variant="body2">{statusMessage}</Typography> : null}
        </Stack>

        {loadError && <Alert severity="error">{getErrorMessage(loadError)}</Alert>}

        <Stack spacing={2}>
          {SCHEDULE_TEMPLATES.map((template) => {
            const templateSchedules = schedulesByEventType.get(template.value) ?? [];
            const isSingleInstance = template.singleInstance ?? true;

            return (
              <Paper key={template.value} sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Stack spacing={0.5}>
                    <Typography variant="h6">{template.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {describeTemplate(template, templateSchedules[0]?.condition)}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                {isSingleInstance ? (
                  <SingleScheduleSummary
                    template={template}
                    schedule={templateSchedules[0] ?? null}
                    onEdit={() => openEditor(template, templateSchedules[0])}
                    onCreate={() => openEditor(template)}
                    onToggle={(schedule) => toggleEnabled(schedule)}
                    isUpdating={isUpdating}
                    updatingScheduleId={updatingScheduleId}
                    isLoading={isLoading}
                    isCreating={isCreating}
                  />
                ) : (
                  <MultiScheduleSummary
                    template={template}
                    schedules={templateSchedules}
                    onEdit={(schedule) => openEditor(template, schedule)}
                    onToggle={(schedule) => toggleEnabled(schedule)}
                    onCreate={() => openEditor(template)}
                    isUpdating={isUpdating}
                    updatingScheduleId={updatingScheduleId}
                    isLoading={isLoading}
                    isCreating={isCreating}
                  />
                )}
              </Paper>
            );
          })}
        </Stack>
      </Stack>

      <Drawer anchor="right" open={Boolean(editingTemplate)} onClose={closeEditor}>
        {editingTemplate ? (
          <Box sx={{ width: { xs: '100vw', sm: 420, md: 480 }, p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">
                {editingScheduleId ? 'Edit schedule' : 'Create schedule'}
              </Typography>
              <IconButton onClick={closeEditor} aria-label="Close editor">
                <Close />
              </IconButton>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {describeTemplate(templateInEditor, formState.condition as Record<string, unknown>)}
            </Typography>

            {createError ? <Alert severity="error">{getErrorMessage(createError)}</Alert> : null}
            {validationError ? <Alert severity="warning">{validationError}</Alert> : null}

            <Stack component="form" spacing={2} onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                label="Schedule name"
                fullWidth
                required
                value={formState.name}
                onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                helperText="A friendly name for this schedule."
              />

              <TextField
                label="Description"
                fullWidth
                value={formState.description}
                onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
              />

              <TextField
                label="Send time"
                type="time"
                fullWidth
                value={formState.send_time}
                onChange={(e) => setFormState((prev) => ({ ...prev, send_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />

              <Autocomplete<TimezoneOption>
                fullWidth
                options={timezoneOptions}
                value={
                  timezoneOptions.find((option) => option.value === currentTimezone) ??
                  createTimezoneOption(currentTimezone)
                }
                onChange={(_, option) =>
                  setFormState((prev) => ({
                    ...prev,
                    timezone: option?.value ?? DEFAULT_TIMEZONE_ID,
                  }))
                }
                isOptionEqualToValue={(option, value) => option.value === value.value}
                groupBy={(option) => (option.featured ? 'Commonly used' : 'All time zones')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Timezone"
                    helperText="Timezone for the send time."
                  />
                )}
                clearOnEscape
              />

              {recipientInputEnabled ? (
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={
                    formState.recipient_addresses
                      ? formState.recipient_addresses.split(/\s+/).filter(Boolean)
                      : []
                  }
                  onChange={(_event, newValue) => {
                    setFormState((prev) => ({
                      ...prev,
                      recipient_addresses: newValue.join(' '),
                    }));
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return <Chip variant="outlined" label={option} key={key} {...tagProps} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Recipient email addresses"
                      fullWidth
                      required={recipientInputRequired && !formState.recipient_addresses}
                      helperText={
                        recipientInputRequired
                          ? 'Press Enter to add an email.'
                          : 'Press Enter to add an email (optional).'
                      }
                    />
                  )}
                />
              ) : (
                <TextField
                  label="Recipients"
                  fullWidth
                  disabled
                  value="Auto-resolved from THR borrowers"
                  helperText="Recipients use borrower email addresses."
                />
              )}

              {templateInEditor.value === 'thr_approaching_return_date_reminder' ? (
                <TextField
                  label="Days before due date"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1, max: 60 }}
                  value={Number(
                    (formState.condition as Record<string, unknown> | undefined)?.['days_out'] ??
                      14,
                  )}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      condition: {
                        ...(prev.condition ?? {}),
                        days_out: Number(e.target.value) || 1,
                      },
                    }))
                  }
                  helperText="Send reminders this many days before due."
                />
              ) : null}

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Cadence
                </Typography>
                <Stack spacing={2} alignItems="flex-start">
                  <ToggleButtonGroup
                    value={formState.frequency}
                    exclusive
                    onChange={(_, value) => {
                      if (value) handleFrequencyChange(value);
                    }}
                    aria-label="frequency"
                    color="primary"
                    size="small"
                  >
                    <ToggleButton value="weekly">Weekly</ToggleButton>
                    <ToggleButton value="monthly">Monthly</ToggleButton>
                    <ToggleButton value="daily">Daily</ToggleButton>
                  </ToggleButtonGroup>

                  {formState.frequency === 'weekly' ? (
                    <ToggleButtonGroup
                      value={formState.days_of_week}
                      onChange={(_, newDays) => {
                        setFormState((prev) => ({ ...prev, days_of_week: newDays }));
                      }}
                      aria-label="days of week"
                      size="small"
                      color="primary"
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                    >
                      {DAYS_OF_WEEK.map((day) => (
                        <ToggleButton key={day.key} value={day.key}>
                          {day.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  ) : formState.frequency === 'monthly' ? (
                    <TextField
                      label="Day of month"
                      type="number"
                      inputProps={{ min: 1, max: 31 }}
                      value={formState.day_of_month ?? 1}
                      onChange={(e) =>
                        setFormState((prev) => ({ ...prev, day_of_month: Number(e.target.value) }))
                      }
                      size="small"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Runs every day at the scheduled time.
                    </Typography>
                  )}
                </Stack>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={formState.enabled}
                    onChange={(e) =>
                      setFormState((prev) => ({ ...prev, enabled: e.target.checked }))
                    }
                  />
                }
                label="Schedule enabled"
              />

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isCreating || isUpdating}
                  aria-label="Save schedule"
                >
                  {editingScheduleId
                    ? isUpdating
                      ? 'Saving…'
                      : 'Save changes'
                    : isCreating
                      ? 'Creating…'
                      : 'Create schedule'}
                </Button>
                <Button variant="text" onClick={closeEditor} disabled={isCreating || isUpdating}>
                  Close
                </Button>
              </Stack>

              {lastSavedMessage ? <Alert severity="success">{lastSavedMessage}</Alert> : null}
            </Stack>
          </Box>
        ) : null}
      </Drawer>
    </>
  );
};

const SmtpSettingsTab: React.FC = () => {
  const { data, isLoading, isFetching, error } = useSmtpConfig();
  const { mutateAsync: saveConfig, isPending } = useSaveSmtpConfig();
  const notifications = useNotifications();
  const [form, setForm] = useState<EmailSmtpConfigInput>({
    host: '',
    port: 587,
    username: '',
    password_secret: '',
    from_name: '',
    from_email: '',
    reply_to: '',
    auth_method: 'login',
    tls_mode: 'starttls',
  });
  const [hasStoredPassword, setHasStoredPassword] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        id: data.id,
        host: data.host,
        port: data.port,
        username: data.username ?? '',
        // Do not hydrate stored passwords into the form; keep write-only.
        password_secret: '',
        from_name: data.from_name ?? '',
        from_email: data.from_email,
        reply_to: data.reply_to ?? '',
        auth_method: data.auth_method,
        tls_mode: data.tls_mode,
      });
      setHasStoredPassword(Boolean(data.has_password));
    } else {
      setHasStoredPassword(false);
    }
  }, [data]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const { password_secret, ...rest } = form;
      const payload: EmailSmtpConfigInput = {
        ...rest,
        port: Number(form.port) || 587,
      };

      // Only send password when the user explicitly entered one; prevents echoing or wiping secrets.
      if (password_secret && password_secret.trim().length > 0) {
        payload.password_secret = password_secret;
      }

      await saveConfig(payload);
      if (payload.password_secret) {
        setHasStoredPassword(true);
      }
      notifications.success('SMTP settings saved');
      // Clear the password field after successful save to keep it write-only.
      setForm((prev) => ({ ...prev, password_secret: '' }));
    } catch (err) {
      const message = getErrorMessage(err) || 'Failed to save SMTP settings';
      notifications.error('Save failed', message);
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 2 }} component="form" onSubmit={handleSave}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5">SMTP Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Store connection details for outbound email. Values are saved in the database.
          </Typography>
        </Box>

        {error ? <Alert severity="error">{getErrorMessage(error)}</Alert> : null}

        {isLoading || (isFetching && !data) ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading current settings…</Typography>
          </Stack>
        ) : null}

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <TextField
            label="SMTP host"
            fullWidth
            required
            value={form.host}
            onChange={(e) => setForm((prev) => ({ ...prev, host: e.target.value }))}
          />
          <TextField
            label="Port"
            type="number"
            fullWidth
            required
            value={form.port}
            onChange={(e) => setForm((prev) => ({ ...prev, port: Number(e.target.value) }))}
          />
          <TextField
            label="TLS"
            select
            fullWidth
            value={form.tls_mode}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                tls_mode: e.target.value as EmailSmtpConfigInput['tls_mode'],
              }))
            }
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="starttls">STARTTLS</MenuItem>
            <MenuItem value="tls">TLS</MenuItem>
          </TextField>
          <TextField
            label="Username"
            fullWidth
            value={form.username}
            onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
          />
          <TextField
            label="Password / App password"
            type="password"
            fullWidth
            value={form.password_secret}
            onChange={(e) => setForm((prev) => ({ ...prev, password_secret: e.target.value }))}
            helperText={
              hasStoredPassword
                ? '✓ Password saved. Leave blank to keep it; type a new value to replace it.'
                : 'We never read back the stored secret.'
            }
          />
          <TextField
            label="From name"
            fullWidth
            value={form.from_name}
            onChange={(e) => setForm((prev) => ({ ...prev, from_name: e.target.value }))}
          />
          <TextField
            label="From email"
            fullWidth
            required
            value={form.from_email}
            onChange={(e) => setForm((prev) => ({ ...prev, from_email: e.target.value }))}
          />
          <TextField
            label="Reply-to"
            fullWidth
            value={form.reply_to}
            onChange={(e) => setForm((prev) => ({ ...prev, reply_to: e.target.value }))}
          />
          <TextField
            label="Auth method"
            select
            fullWidth
            value={form.auth_method}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                auth_method: e.target.value as EmailSmtpConfigInput['auth_method'],
              }))
            }
          >
            <MenuItem value="plain">PLAIN</MenuItem>
            <MenuItem value="login">LOGIN</MenuItem>
            <MenuItem value="cram_md5">CRAM-MD5</MenuItem>
          </TextField>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained" disabled={isPending}>
            {isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

const OutboxTab: React.FC<{ active?: boolean }> = ({ active = false }) => {
  const {
    data: emails = [],
    isLoading,
    error,
  } = useOutboundEmails({
    refetchIntervalMs: active ? 10000 : undefined,
  });

  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);

  useEffect(() => {
    if (emails.length === 0) {
      setSelectedEmailId(null);
      return;
    }

    // Keep a selection when the list refreshes; fall back to the first row.
    const stillExists = emails.some((email) => email.id === selectedEmailId);
    if (!selectedEmailId || !stillExists) {
      setSelectedEmailId(emails[0]?.id ?? null);
    }
  }, [emails, selectedEmailId]);

  const selectedEmail = useMemo(
    () => emails.find((email) => email.id === selectedEmailId) ?? null,
    [emails, selectedEmailId],
  );

  const isHtml = (value?: string | null): boolean =>
    Boolean(value && /<\s*([a-z][^\s>]*)(?:\s|>)/i.test(value));

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Outbound email log</Typography>
        {isLoading ? <Typography variant="body2">Loading…</Typography> : null}
      </Stack>
      {error ? <Alert severity="error">{getErrorMessage(error)}</Alert> : null}
      {emails.length === 0 && !isLoading ? (
        <Typography variant="body2" color="text.secondary">
          No outbound emails recorded yet.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Paper variant="outlined" sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <List disablePadding>
              {emails.map((email) => (
                <ListItemButton
                  key={email.id}
                  selected={email.id === selectedEmailId}
                  onClick={() => setSelectedEmailId(email.id)}
                  divider
                  alignItems="flex-start"
                >
                  <ListItemText
                    primary={email.subject || '(No subject)'}
                    primaryTypographyProps={{
                      variant: 'subtitle2',
                      noWrap: true,
                      sx: { mb: 0.5 },
                    }}
                    secondary={
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" noWrap sx={{ maxWidth: '65%' }}>
                            {email.recipient_email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(email.created_at).fromNow()}
                          </Typography>
                        </Stack>
                        <Box>
                          <Chip
                            size="small"
                            label={email.status}
                            color={email.status === 'sent' ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, minHeight: 320 }}>
            {!selectedEmail ? (
              <Typography variant="body2" color="text.secondary">
                Select an email to view its details.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                <Stack spacing={0.5}>
                  <Typography variant="overline" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography variant="h6">{selectedEmail.subject ?? '—'}</Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                  <Stack spacing={0.25}>
                    <Typography variant="overline" color="text.secondary">
                      To
                    </Typography>
                    <Typography>{selectedEmail.recipient_email ?? '—'}</Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="overline" color="text.secondary">
                      Created
                    </Typography>
                    <Typography>
                      {dayjs(selectedEmail.created_at).format('MMM D, YYYY h:mm A')}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="overline" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      size="small"
                      label={`${selectedEmail.status} (attempt ${selectedEmail.attempt})`}
                      color={selectedEmail.status === 'sent' ? 'success' : 'error'}
                    />
                  </Stack>
                </Stack>

                {selectedEmail.error ? <Alert severity="error">{selectedEmail.error}</Alert> : null}

                <OutboundEmailAttachmentList attachments={selectedEmail.attachments ?? []} />

                <Stack spacing={0.5}>
                  <Typography variant="overline" color="text.secondary">
                    Body preview
                  </Typography>
                  {isHtml(selectedEmail.body_preview) ? (
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, bgcolor: 'grey.50', mb: 1.5, overflow: 'auto' }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Rendered
                      </Typography>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1.25,
                          // Emails render on white; pin light colors so the preview
                          // stays legible regardless of the app's dark/light theme.
                          backgroundColor: '#ffffff',
                          color: 'rgba(0, 0, 0, 0.87)',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(selectedEmail.body_preview ?? ''),
                        }}
                      />
                    </Paper>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' }}
                    >
                      <Typography
                        sx={{ color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {selectedEmail.body_preview?.trim() || '—'}
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </Stack>
            )}
          </Paper>
        </Box>
      )}
    </Paper>
  );
};

type TemplatePart = { type: 'text' | 'token'; value: string };

const TEMPLATE_TOKEN_RE = /(\{\{\s*[A-Za-z0-9_.]+\s*\}\})/g;
const TEMPLATE_TOKEN_EXACT_RE = /^\{\{\s*[A-Za-z0-9_.]+\s*\}\}$/;

const parseTemplateParts = (value: string): TemplatePart[] =>
  value
    .split(TEMPLATE_TOKEN_RE)
    .map((segment) =>
      TEMPLATE_TOKEN_EXACT_RE.test(segment)
        ? ({ type: 'token', value: segment } as TemplatePart)
        : ({ type: 'text', value: segment } as TemplatePart),
    );

const reassembleParts = (parts: TemplatePart[]): string => parts.map((part) => part.value).join('');

const humanizeTemplateKey = (key: string): string =>
  key
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');

const templateIdentity = (template: EmailTemplate): string =>
  `${template.key}::${template.locale}::${template.version}`;

const TokenChip: React.FC<{ value: string }> = ({ value }) => (
  <Chip
    label={value.replace(/\s+/g, '')}
    size="small"
    variant="outlined"
    color="primary"
    sx={{ fontFamily: 'monospace', fontSize: 12, cursor: 'not-allowed' }}
  />
);

const TemplatesTab: React.FC = () => {
  const { data: templates, isLoading, isFetching, error } = useEmailTemplates();
  const { mutateAsync: updateTemplate, isPending } = useUpdateEmailTemplate();
  const notifications = useNotifications();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [subjectParts, setSubjectParts] = useState<TemplatePart[]>([]);
  const [bodyParts, setBodyParts] = useState<TemplatePart[]>([]);
  const [enabled, setEnabled] = useState(true);

  const selected = useMemo(
    () => templates?.find((template) => templateIdentity(template) === selectedId) ?? null,
    [templates, selectedId],
  );

  useEffect(() => {
    const first = templates?.[0];
    if (!selectedId && first) {
      setSelectedId(templateIdentity(first));
    }
  }, [templates, selectedId]);
  useEffect(() => {
    if (!selected) return;
    setSubjectParts(parseTemplateParts(selected.subject_template));
    setBodyParts(parseTemplateParts(selected.body_template));
    setEnabled(selected.enabled);
    // Re-hydrate the editor whenever the selected template identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, templates]);

  const reassembledSubject = reassembleParts(subjectParts);
  const reassembledBody = reassembleParts(bodyParts);
  const isHtml = (selected?.content_type ?? '').toLowerCase().includes('html');

  const isDirty =
    !!selected &&
    (reassembledSubject !== selected.subject_template ||
      reassembledBody !== selected.body_template ||
      enabled !== selected.enabled);

  const updateTextPart = (
    setter: React.Dispatch<React.SetStateAction<TemplatePart[]>>,
    index: number,
    value: string,
  ) => {
    setter((prev) => prev.map((part, i) => (i === index ? { ...part, value } : part)));
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      await updateTemplate({
        key: selected.key,
        locale: selected.locale,
        version: selected.version,
        subject_template: reassembledSubject,
        body_template: reassembledBody,
        enabled,
      });
      notifications.success('Template saved');
    } catch (err) {
      const message = getErrorMessage(err) || 'Failed to save template';
      notifications.error('Save failed', message);
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5">Email Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Edit the wording of notification emails. Tokens shown as locked grey chips (for example{' '}
            <code>{'{{item_count}}'}</code>) are inserted automatically when the email is sent and
            cannot be changed.
          </Typography>
        </Box>

        {error ? <Alert severity="error">{getErrorMessage(error)}</Alert> : null}

        {isLoading || (isFetching && !templates) ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading templates…</Typography>
          </Stack>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
            alignItems: 'start',
          }}
        >
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <List disablePadding sx={{ maxHeight: 560, overflowY: 'auto' }}>
              {(templates ?? []).map((template) => {
                const id = templateIdentity(template);
                return (
                  <ListItemButton
                    key={id}
                    selected={id === selectedId}
                    onClick={() => setSelectedId(id)}
                    divider
                  >
                    <ListItemText
                      primary={humanizeTemplateKey(template.key)}
                      secondary={`${template.locale} · v${template.version}`}
                    />
                    {!template.enabled ? (
                      <Chip label="Disabled" size="small" color="default" />
                    ) : null}
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          {selected ? (
            <Stack spacing={2.5}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Box>
                  <Typography variant="h6">{humanizeTemplateKey(selected.key)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selected.key} · {selected.locale} · v{selected.version} · {selected.content_type}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                  }
                  label="Enabled"
                />
              </Stack>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Subject
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                    {subjectParts.map((part, index) =>
                      part.type === 'token' ? (
                        <TokenChip key={`subject-${index}`} value={part.value} />
                      ) : (
                        <TextField
                          key={`subject-${index}`}
                          size="small"
                          variant="outlined"
                          value={part.value}
                          onChange={(e) => updateTextPart(setSubjectParts, index, e.target.value)}
                          sx={{ width: `${Math.max(6, part.value.length + 3)}ch` }}
                        />
                      ),
                    )}
                  </Box>
                </Paper>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Body
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    {bodyParts.map((part, index) =>
                      part.type === 'token' ? (
                        <Box key={`body-${index}`}>
                          <TokenChip value={part.value} />
                        </Box>
                      ) : (
                        <TextField
                          key={`body-${index}`}
                          multiline
                          minRows={2}
                          fullWidth
                          value={part.value}
                          onChange={(e) => updateTextPart(setBodyParts, index, e.target.value)}
                          slotProps={{
                            input: { sx: { fontFamily: 'monospace', fontSize: 13 } },
                          }}
                        />
                      ),
                    )}
                  </Stack>
                </Paper>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Preview
                </Typography>
                {isHtml ? (
                  <Paper
                    variant="outlined"
                    // Emails render on a white background; pin light colors so the
                    // preview stays legible regardless of the app's dark/light theme.
                    sx={{ p: 1.5, bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(reassembledBody) }}
                  />
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' }}
                  >
                    <Typography
                      sx={{ color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {reassembledBody.trim() || '—'}
                    </Typography>
                  </Paper>
                )}
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!isDirty || isPending}
                >
                  {isPending ? 'Saving…' : 'Save template'}
                </Button>
                <Button
                  variant="text"
                  disabled={!isDirty || isPending}
                  onClick={() => {
                    setSubjectParts(parseTemplateParts(selected.subject_template));
                    setBodyParts(parseTemplateParts(selected.body_template));
                    setEnabled(selected.enabled);
                  }}
                >
                  Reset
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a template to edit.
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

export const EmailSchedulesPage: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Email Management');
  const [tab, setTab] = useState<'schedules' | 'smtp' | 'outbox' | 'templates'>('schedules');

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">Email Management</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MailOutline color="primary" sx={{ fontSize: 36 }} />
          <Typography variant="h4" component="h1">
            Email Management
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          Emails are delivered via the SMTP server configured on the SMTP tab. When no SMTP host is
          configured, delivery is skipped. Every notification is also recorded in the outbox so you
          can verify rendered subjects and bodies.
        </Alert>
      </Box>

      <Paper sx={{ mb: 2, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }} square={false}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value as 'schedules' | 'smtp' | 'outbox' | 'templates')}
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label="Schedules"
            value="schedules"
            icon={<ScheduleSend fontSize="small" />}
            iconPosition="start"
          />
          <Tab label="SMTP Settings" value="smtp" />
          <Tab label="Templates" value="templates" />
          <Tab label="Outbox" value="outbox" />
        </Tabs>
      </Paper>

      {tab === 'schedules' && <SchedulesTab />}
      {tab === 'smtp' && <SmtpSettingsTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'outbox' && <OutboxTab active />}
    </Container>
  );
};
